import React, { useState, useEffect } from 'react';
import { Button, Row, Col, Card, Statistic, Typography, Space, Modal, Tag, Avatar } from 'antd';
import {
    ThunderboltOutlined,
    RocketOutlined,
    SafetyOutlined,
    CloudOutlined,
    ExperimentOutlined,
    PlayCircleOutlined
} from '@ant-design/icons';
import { XAxis, YAxis, CartesianGrid, ResponsiveContainer, AreaChart, Area, Line } from 'recharts';
import LoginPage from './LoginPage';

// 风力动画样式 - 参考Windy.com风格
const windAnimationStyles = `
  @keyframes float {
    0%, 100% { transform: translateY(0px) translateX(0px); }
    25% { transform: translateY(-20px) translateX(10px); }
    50% { transform: translateY(-10px) translateX(20px); }
    75% { transform: translateY(-30px) translateX(5px); }
  }

  @keyframes drift {
    0% { transform: translateX(-200px); }
    100% { transform: translateX(calc(100vw + 200px)); }
  }

  @keyframes windFlow {
    0% { 
      transform: translateX(-100px) scaleX(0.8); 
      opacity: 0; 
    }
    20% { 
      opacity: 1; 
      transform: translateX(0px) scaleX(1); 
    }
    80% { 
      opacity: 1; 
      transform: translateX(150px) scaleX(1.2); 
    }
    100% { 
      transform: translateX(300px) scaleX(0.8); 
      opacity: 0; 
    }
  }

  @keyframes windStreams {
    0% { 
      stroke-dashoffset: 100; 
      opacity: 0.3; 
    }
    50% { 
      stroke-dashoffset: 0; 
      opacity: 1; 
    }
    100% { 
      stroke-dashoffset: -100; 
      opacity: 0.3; 
    }
  }

  @keyframes turbine {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @keyframes cloudFloat {
    0% { 
      transform: translateX(-300px) translateY(0px); 
      opacity: 0.4; 
    }
    10% { 
      opacity: 0.8; 
    }
    90% { 
      opacity: 0.8; 
    }
    100% { 
      transform: translateX(calc(100vw + 100px)) translateY(-20px); 
      opacity: 0.4; 
    }
  }

  .cloud {
    position: absolute;
    background: radial-gradient(ellipse at center, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.6) 50%, rgba(255, 255, 255, 0.2) 100%);
    border-radius: 50px;
    opacity: 0.7;
    animation: cloudFloat linear infinite;
    filter: blur(1px);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
  }

  .cloud:before {
    content: '';
    position: absolute;
    background: radial-gradient(ellipse at center, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.4) 100%);
    border-radius: 50px;
    filter: blur(0.5px);
  }

  .cloud:after {
    content: '';
    position: absolute;
    background: radial-gradient(ellipse at center, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.4) 100%);
    border-radius: 50px;
    filter: blur(0.5px);
  }

  .cloud1 {
    width: 120px;
    height: 60px;
    top: 15%;
    animation-duration: 45s;
    animation-delay: 0s;
  }

  .cloud1:before {
    width: 80px;
    height: 80px;
    top: -40px;
    left: 15px;
  }

  .cloud1:after {
    width: 90px;
    height: 50px;
    top: -25px;
    right: 15px;
  }

  .cloud2 {
    width: 90px;
    height: 45px;
    top: 35%;
    animation-duration: 60s;
    animation-delay: 15s;
  }

  .cloud2:before {
    width: 60px;
    height: 60px;
    top: -30px;
    left: 10px;
  }

  .cloud2:after {
    width: 70px;
    height: 40px;
    top: -20px;
    right: 10px;
  }

  .cloud3 {
    width: 150px;
    height: 75px;
    top: 8%;
    animation-duration: 80s;
    animation-delay: 30s;
  }

  .cloud3:before {
    width: 100px;
    height: 100px;
    top: -50px;
    left: 25px;
  }

  .cloud3:after {
    width: 110px;
    height: 60px;
    top: -30px;
    right: 25px;
  }

  .cloud4 {
    width: 70px;
    height: 35px;
    top: 55%;
    animation-duration: 50s;
    animation-delay: 45s;
  }

  .cloud4:before {
    width: 45px;
    height: 45px;
    top: -22px;
    left: 8px;
  }

  .cloud4:after {
    width: 55px;
    height: 30px;
    top: -15px;
    right: 8px;
  }

  .wind-field {
    position: absolute;
    width: 100%;
    height: 100%;
    pointer-events: none;
    overflow: hidden;
  }

  .wind-stream {
    position: absolute;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(135, 206, 250, 0.8), rgba(255, 255, 255, 0.9), rgba(135, 206, 250, 0.8), transparent);
    animation: windFlow linear infinite;
    border-radius: 1px;
    box-shadow: 0 0 3px rgba(135, 206, 250, 0.5);
  }

  .wind-stream:nth-child(1) {
    top: 20%;
    left: 0;
    width: 150px;
    animation-duration: 4s;
    animation-delay: 0s;
  }

  .wind-stream:nth-child(2) {
    top: 28%;
    left: 0;
    width: 200px;
    animation-duration: 5s;
    animation-delay: 1.2s;
  }

  .wind-stream:nth-child(3) {
    top: 36%;
    left: 0;
    width: 120px;
    animation-duration: 3.5s;
    animation-delay: 2.5s;
  }

  .wind-stream:nth-child(4) {
    top: 44%;
    left: 0;
    width: 180px;
    animation-duration: 4.5s;
    animation-delay: 0.8s;
  }

  .wind-stream:nth-child(5) {
    top: 52%;
    left: 0;
    width: 160px;
    animation-duration: 4.2s;
    animation-delay: 1.8s;
  }

  .wind-stream:nth-child(6) {
    top: 60%;
    left: 0;
    width: 140px;
    animation-duration: 3.8s;
    animation-delay: 2.8s;
  }

  .wind-stream:nth-child(7) {
    top: 68%;
    left: 0;
    width: 190px;
    animation-duration: 4.8s;
    animation-delay: 0.3s;
  }

  .wind-stream:nth-child(8) {
    top: 76%;
    left: 0;
    width: 130px;
    animation-duration: 3.7s;
    animation-delay: 1.5s;
  }

  .wind-vector {
    position: absolute;
    width: 100%;
    height: 100%;
    opacity: 0.6;
  }

  .wind-arrow {
    position: absolute;
    width: 20px;
    height: 1px;
    background: rgba(255, 255, 255, 0.7);
    animation: windFlow linear infinite;
  }

  .wind-arrow:before {
    content: '';
    position: absolute;
    right: 0;
    top: -2px;
    width: 0;
    height: 0;
    border-left: 4px solid rgba(255, 255, 255, 0.7);
    border-top: 2px solid transparent;
    border-bottom: 2px solid transparent;
  }

  .floating-icon {
    position: absolute;
    animation: float 6s ease-in-out infinite;
    opacity: 0.3;
    color: rgba(255, 255, 255, 0.6);
  }

  .floating-icon:nth-child(1) {
    top: 15%;
    left: 10%;
    animation-delay: 0s;
  }

  .floating-icon:nth-child(2) {
    top: 25%;
    right: 15%;
    animation-delay: 2s;
  }

  .floating-icon:nth-child(3) {
    top: 45%;
    left: 5%;
    animation-delay: 4s;
  }

  .floating-icon:nth-child(4) {
    top: 65%;
    right: 10%;
    animation-delay: 1s;
  }

  .turbine-icon {
    animation: turbine 3s linear infinite;
  }

  .wind-turbine {
    position: absolute;
    opacity: 0.2;
    color: rgba(255, 255, 255, 0.4);
  }

  .wind-turbine-1 {
    top: 30%;
    right: 20%;
    font-size: 48px;
    animation: turbine 4s linear infinite;
  }

  .wind-turbine-2 {
    top: 60%;
    left: 15%;
    font-size: 36px;
    animation: turbine 5s linear infinite reverse;
  }

  .wind-turbine-3 {
    top: 20%;
    left: 80%;
    font-size: 28px;
    animation: turbine 3.5s linear infinite;
  }

  .particle {
    position: absolute;
    width: 4px;
    height: 4px;
    background: rgba(255, 255, 255, 0.6);
    border-radius: 50%;
    animation: particleFloat 8s ease-in-out infinite;
  }

  @keyframes particleFloat {
    0%, 100% { 
      transform: translateY(0px) translateX(0px); 
      opacity: 0;
    }
    10% { opacity: 1; }
    50% { 
      transform: translateY(-100px) translateX(50px); 
      opacity: 0.8;
    }
    90% { opacity: 1; }
  }

  .particle:nth-child(1) {
    top: 80%;
    left: 10%;
    animation-delay: 0s;
  }

  .particle:nth-child(2) {
    top: 85%;
    left: 30%;
    animation-delay: 2s;
  }

  .particle:nth-child(3) {
    top: 75%;
    left: 60%;
    animation-delay: 4s;
  }

  .particle:nth-child(4) {
    top: 90%;
    left: 80%;
    animation-delay: 6s;
  }
`;

const { Title, Paragraph, Text } = Typography;

const LandingPage: React.FC = () => {
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [animatedValue, setAnimatedValue] = useState(0);

    // 实时数据动画效果
    useEffect(() => {
        const interval = setInterval(() => {
            setAnimatedValue(prev => (prev + 1) % 100);
        }, 100);
        return () => clearInterval(interval);
    }, []);

    // 注入动画样式
    useEffect(() => {
        const styleSheet = document.createElement("style");
        styleSheet.type = "text/css";
        styleSheet.innerText = windAnimationStyles;
        document.head.appendChild(styleSheet);

        return () => {
            document.head.removeChild(styleSheet);
        };
    }, []);

    // 模拟实时功率数据
    const generateRealTimeData = () => {
        const data = [];
        const now = Date.now();
        for (let i = -23; i <= 0; i++) {
            const time = new Date(now + i * 3600000);
            const hour = time.getHours();
            let power;
            if (hour >= 6 && hour <= 18) {
                power = 800 + Math.sin(hour * Math.PI / 12) * 300 + Math.random() * 100;
            } else {
                power = 400 + Math.random() * 200;
            }
            data.push({
                time: `${hour}:00`,
                power: Math.round(power),
                predicted: Math.round(power + (Math.random() - 0.5) * 50)
            });
        }
        return data;
    };

    const realTimeData = generateRealTimeData();

    const features = [
        {
            icon: <ThunderboltOutlined style={{ fontSize: '48px', color: '#1890ff' }} />,
            title: '高精度预测',
            description: '基于深度学习算法，预测精度达到94.2%以上',
            highlight: '准确率 > 94%'
        },
        {
            icon: <CloudOutlined style={{ fontSize: '48px', color: '#52c41a' }} />,
            title: '实时数据处理',
            description: '支持海量气象数据实时采集与处理',
            highlight: '毫秒级响应'
        },
        {
            icon: <ExperimentOutlined style={{ fontSize: '48px', color: '#faad14' }} />,
            title: '智能模型管理',
            description: '自动化模型训练、优化和部署',
            highlight: '全自动化'
        },
        {
            icon: <SafetyOutlined style={{ fontSize: '48px', color: '#f5222d' }} />,
            title: '可靠性保障',
            description: '7×24小时稳定运行，99.9%可用性',
            highlight: '99.9% 可用性'
        }
    ];

    const techHighlights = [
        { label: '支持电站数量', value: '1000+', suffix: '座' },
        { label: '日处理数据量', value: '10TB+', suffix: '' },
        { label: '预测时间范围', value: '24', suffix: '小时' },
        { label: '模型响应时间', value: '<100', suffix: 'ms' }
    ];

    // 风力动画组件 - Windy风格
    const WindAnimation: React.FC = () => (
        <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: 1
        }}>
            {/* 云图层 - 多层云朵营造深度感 */}
            <div className="cloud cloud1"></div>
            <div className="cloud cloud2"></div>
            <div className="cloud cloud3"></div>
            <div className="cloud cloud4"></div>

            {/* 风场流线 - 模拟Windy的风向可视化 */}
            <div className="wind-field">
                <div className="wind-stream"></div>
                <div className="wind-stream"></div>
                <div className="wind-stream"></div>
                <div className="wind-stream"></div>
                <div className="wind-stream"></div>
                <div className="wind-stream"></div>
                <div className="wind-stream"></div>
                <div className="wind-stream"></div>
            </div>

            {/* 风向矢量 */}
            <div className="wind-vector">
                {Array.from({ length: 12 }, (_, i) => (
                    <div
                        key={i}
                        className="wind-arrow"
                        style={{
                            top: `${15 + i * 6}%`,
                            left: `${10 + (i % 3) * 30}%`,
                            animationDelay: `${i * 0.5}s`,
                            animationDuration: `${4 + (i % 3)}s`
                        }}
                    />
                ))}
            </div>

            {/* 气象要素图标 */}
            <div className="floating-icon">
                <CloudOutlined style={{ fontSize: '40px', color: 'rgba(135, 206, 250, 0.8)' }} />
            </div>
            <div className="floating-icon">
                <ThunderboltOutlined style={{ fontSize: '32px', color: 'rgba(255, 215, 0, 0.8)' }} />
            </div>
            <div className="floating-icon">
                <div className="turbine-icon">
                    <ExperimentOutlined style={{ fontSize: '28px', color: 'rgba(144, 238, 144, 0.8)' }} />
                </div>
            </div>
            <div className="floating-icon">
                <CloudOutlined style={{ fontSize: '36px', color: 'rgba(176, 196, 222, 0.8)' }} />
            </div>

            {/* 风力发电机组 */}
            <div className="wind-turbine wind-turbine-1">
                <div className="turbine-icon" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>🌪️</div>
            </div>
            <div className="wind-turbine wind-turbine-2">
                <div className="turbine-icon" style={{ color: 'rgba(135, 206, 250, 0.6)' }}>💨</div>
            </div>
            <div className="wind-turbine wind-turbine-3">
                <div className="turbine-icon" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>🌬️</div>
            </div>

            {/* 大气粒子效果 */}
            <div className="particle"></div>
            <div className="particle"></div>
            <div className="particle"></div>
            <div className="particle"></div>
        </div>
    );

    return (
        <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            minHeight: '100vh',
            color: 'white',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* 风力动画背景 */}
            <WindAnimation />

            {/* 顶部导航 */}
            <div style={{
                padding: '16px 24px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                borderBottom: '1px solid rgba(255,255,255,0.2)',
                position: 'relative',
                zIndex: 10
            }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <ThunderboltOutlined style={{ fontSize: '32px', marginRight: '12px' }} />
                    <Title level={3} style={{ color: 'white', margin: 0 }}>
                        电力预测AI系统
                    </Title>
                </div>
                <Space>
                    <Button type="text" style={{ color: 'white' }}>功能特色</Button>
                    <Button type="text" style={{ color: 'white' }}>技术优势</Button>
                    <Button type="text" style={{ color: 'white' }}>解决方案</Button>
                    <Button
                        type="primary"
                        size="large"
                        icon={<PlayCircleOutlined />}
                        onClick={() => setShowLoginModal(true)}
                        style={{
                            background: 'rgba(255,255,255,0.2)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            backdropFilter: 'blur(10px)'
                        }}
                    >
                        立即试用
                    </Button>
                </Space>
            </div>

            {/* Hero区域 */}
            <div style={{ padding: '80px 24px', textAlign: 'center', position: 'relative', zIndex: 10 }}>
                <Title level={1} style={{
                    color: 'white',
                    fontSize: '64px',
                    marginBottom: '24px',
                    textShadow: '0 4px 8px rgba(0,0,0,0.3)'
                }}>
                    智能化新能源
                    <br />
                    <span style={{ color: '#ffd700' }}>功率预测平台</span>
                </Title>
                <Paragraph style={{
                    fontSize: '24px',
                    color: 'rgba(255,255,255,0.9)',
                    maxWidth: '800px',
                    margin: '0 auto 32px'
                }}>
                    基于人工智能和大数据技术，为风电、光伏、水电等新能源发电提供精准的功率预测服务，
                    助力能源转型，构建智慧电网
                </Paragraph>

                {/* 实时气象数据展示 - Windy风格 */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '24px',
                    marginBottom: '40px',
                    flexWrap: 'wrap'
                }}>
                    <div style={{
                        background: 'rgba(135, 206, 250, 0.15)',
                        backdropFilter: 'blur(15px)',
                        padding: '16px 20px',
                        borderRadius: '16px',
                        border: '1px solid rgba(135, 206, 250, 0.3)',
                        textAlign: 'center',
                        minWidth: '110px',
                        boxShadow: '0 8px 16px rgba(135, 206, 250, 0.1)'
                    }}>
                        <div style={{ fontSize: '24px', marginBottom: '6px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>🌡️</div>
                        <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#87CEEB', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                            {(22 + Math.sin(animatedValue * 0.1) * 3).toFixed(1)}°C
                        </div>
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontWeight: '500' }}>气温</div>
                    </div>

                    <div style={{
                        background: 'rgba(144, 238, 144, 0.15)',
                        backdropFilter: 'blur(15px)',
                        padding: '16px 20px',
                        borderRadius: '16px',
                        border: '1px solid rgba(144, 238, 144, 0.3)',
                        textAlign: 'center',
                        minWidth: '110px',
                        boxShadow: '0 8px 16px rgba(144, 238, 144, 0.1)'
                    }}>
                        <div style={{ fontSize: '24px', marginBottom: '6px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>💨</div>
                        <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#98FB98', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                            {(8.5 + Math.sin(animatedValue * 0.15) * 2).toFixed(1)} m/s
                        </div>
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontWeight: '500' }}>风速</div>
                    </div>

                    <div style={{
                        background: 'rgba(255, 255, 255, 0.12)',
                        backdropFilter: 'blur(15px)',
                        padding: '16px 20px',
                        borderRadius: '16px',
                        border: '1px solid rgba(255, 255, 255, 0.25)',
                        textAlign: 'center',
                        minWidth: '110px',
                        boxShadow: '0 8px 16px rgba(255, 255, 255, 0.08)'
                    }}>
                        <div style={{ fontSize: '24px', marginBottom: '6px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>☁️</div>
                        <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#F0F8FF', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                            {Math.max(10, 35 + Math.sin(animatedValue * 0.08) * 15).toFixed(0)}%
                        </div>
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontWeight: '500' }}>云量</div>
                    </div>

                    <div style={{
                        background: 'rgba(255, 215, 0, 0.15)',
                        backdropFilter: 'blur(15px)',
                        padding: '16px 20px',
                        borderRadius: '16px',
                        border: '1px solid rgba(255, 215, 0, 0.3)',
                        textAlign: 'center',
                        minWidth: '110px',
                        boxShadow: '0 8px 16px rgba(255, 215, 0, 0.1)'
                    }}>
                        <div style={{ fontSize: '24px', marginBottom: '6px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>🌞</div>
                        <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#FFD700', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                            {Math.max(200, 850 + Math.sin(animatedValue * 0.12) * 100).toFixed(0)}
                        </div>
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontWeight: '500' }}>W/m²</div>
                    </div>
                </div>

                <Space size="large">
                    <Button
                        type="primary"
                        size="large"
                        icon={<RocketOutlined />}
                        onClick={() => setShowLoginModal(true)}
                        style={{
                            height: '56px',
                            fontSize: '18px',
                            padding: '0 32px',
                            background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
                            border: 'none',
                            boxShadow: '0 8px 16px rgba(238, 90, 36, 0.3)'
                        }}
                    >
                        开始试用
                    </Button>
                    <Button
                        size="large"
                        style={{
                            height: '56px',
                            fontSize: '18px',
                            padding: '0 32px',
                            background: 'rgba(255,255,255,0.1)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            color: 'white',
                            backdropFilter: 'blur(10px)'
                        }}
                    >
                        了解更多
                    </Button>
                </Space>

                {/* 实时数据展示 */}
                <div style={{
                    marginTop: '60px',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '20px',
                    padding: '32px',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    maxWidth: '900px',
                    margin: '60px auto 0'
                }}>
                    <Row gutter={[32, 24]} style={{ marginBottom: '24px' }}>
                        <Col span={6}>
                            <Statistic
                                title={<span style={{ color: 'rgba(255,255,255,0.8)' }}>当前总功率</span>}
                                value={1285.6 + animatedValue * 2}
                                precision={1}
                                suffix="MW"
                                valueStyle={{ color: '#ffd700', fontSize: '24px' }}
                            />
                        </Col>
                        <Col span={6}>
                            <Statistic
                                title={<span style={{ color: 'rgba(255,255,255,0.8)' }}>预测精度</span>}
                                value={94.2 + (animatedValue % 20) * 0.1}
                                precision={1}
                                suffix="%"
                                valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                            />
                        </Col>
                        <Col span={6}>
                            <Statistic
                                title={<span style={{ color: 'rgba(255,255,255,0.8)' }}>在线电站</span>}
                                value={156}
                                suffix="座"
                                valueStyle={{ color: '#1890ff', fontSize: '24px' }}
                            />
                        </Col>
                        <Col span={6}>
                            <Statistic
                                title={<span style={{ color: 'rgba(255,255,255,0.8)' }}>数据更新</span>}
                                value="实时"
                                valueStyle={{ color: '#ff4d4f', fontSize: '24px' }}
                            />
                        </Col>
                    </Row>

                    <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={realTimeData}>
                            <defs>
                                <linearGradient id="colorPower" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#1890ff" stopOpacity={0.8} />
                                    <stop offset="95%" stopColor="#1890ff" stopOpacity={0.1} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.2)" />
                            <XAxis
                                dataKey="time"
                                stroke="rgba(255,255,255,0.8)"
                                tick={{ fill: 'rgba(255,255,255,0.8)' }}
                            />
                            <YAxis
                                stroke="rgba(255,255,255,0.8)"
                                tick={{ fill: 'rgba(255,255,255,0.8)' }}
                            />
                            <Area
                                type="monotone"
                                dataKey="power"
                                stroke="#1890ff"
                                fillOpacity={1}
                                fill="url(#colorPower)"
                                strokeWidth={2}
                            />
                            <Line
                                type="monotone"
                                dataKey="predicted"
                                stroke="#ffd700"
                                strokeWidth={2}
                                strokeDasharray="5 5"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* 功能特色 */}
            <div style={{
                background: 'rgba(255,255,255,0.05)',
                padding: '80px 24px',
                backdropFilter: 'blur(10px)',
                position: 'relative',
                zIndex: 10
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    <Title level={2} style={{
                        color: 'white',
                        textAlign: 'center',
                        marginBottom: '60px',
                        fontSize: '48px'
                    }}>
                        核心功能特色
                    </Title>

                    <Row gutter={[32, 32]}>
                        {features.map((feature, index) => (
                            <Col xs={24} sm={12} lg={6} key={index}>
                                <Card
                                    style={{
                                        background: 'rgba(255,255,255,0.1)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        borderRadius: '16px',
                                        backdropFilter: 'blur(20px)',
                                        textAlign: 'center',
                                        height: '280px',
                                        transition: 'all 0.3s ease'
                                    }}
                                    bodyStyle={{ padding: '32px 24px' }}
                                    hoverable
                                >
                                    <div style={{ marginBottom: '24px' }}>
                                        {feature.icon}
                                    </div>
                                    <Title level={4} style={{ color: 'white', marginBottom: '16px' }}>
                                        {feature.title}
                                    </Title>
                                    <Paragraph style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '16px' }}>
                                        {feature.description}
                                    </Paragraph>
                                    <Tag
                                        color="gold"
                                        style={{
                                            fontSize: '12px',
                                            padding: '4px 12px',
                                            borderRadius: '16px'
                                        }}
                                    >
                                        {feature.highlight}
                                    </Tag>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>
            </div>

            {/* 技术亮点 */}
            <div style={{ padding: '80px 24px', position: 'relative', zIndex: 10 }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    <Title level={2} style={{
                        color: 'white',
                        textAlign: 'center',
                        marginBottom: '60px',
                        fontSize: '48px'
                    }}>
                        技术实力展示
                    </Title>

                    <Row gutter={[32, 32]} justify="center">
                        {techHighlights.map((item, index) => (
                            <Col xs={12} sm={6} key={index}>
                                <div style={{
                                    textAlign: 'center',
                                    background: 'rgba(255,255,255,0.1)',
                                    borderRadius: '16px',
                                    padding: '32px 24px',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    backdropFilter: 'blur(20px)'
                                }}>
                                    <Text style={{
                                        color: 'rgba(255,255,255,0.8)',
                                        fontSize: '16px',
                                        display: 'block',
                                        marginBottom: '12px'
                                    }}>
                                        {item.label}
                                    </Text>
                                    <Title level={2} style={{
                                        color: '#ffd700',
                                        margin: 0,
                                        fontSize: '36px'
                                    }}>
                                        {item.value}
                                        <span style={{ fontSize: '18px', color: 'rgba(255,255,255,0.8)' }}>
                                            {item.suffix}
                                        </span>
                                    </Title>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </div>
            </div>

            {/* 立即体验区域 */}
            <div style={{
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                padding: '80px 24px',
                textAlign: 'center',
                position: 'relative',
                zIndex: 10
            }}>
                <Title level={2} style={{ color: 'white', marginBottom: '24px', fontSize: '48px' }}>
                    立即开始体验
                </Title>
                <Paragraph style={{
                    fontSize: '20px',
                    color: 'rgba(255,255,255,0.9)',
                    marginBottom: '40px',
                    maxWidth: '600px',
                    margin: '0 auto 40px'
                }}>
                    选择不同的用户角色，体验完整的功率预测系统功能
                </Paragraph>

                <Row gutter={[24, 24]} justify="center" style={{ marginBottom: '40px' }}>
                    <Col>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            <Avatar style={{ backgroundColor: '#f56a00', marginRight: '8px' }}>管</Avatar>
                            <Text style={{ color: 'white', fontSize: '16px' }}>系统管理员 - 完整功能体验</Text>
                        </div>
                    </Col>
                    <Col>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            <Avatar style={{ backgroundColor: '#1890ff', marginRight: '8px' }}>屏</Avatar>
                            <Text style={{ color: 'white', fontSize: '16px' }}>数据大屏 - 监控面板</Text>
                        </div>
                    </Col>
                    <Col>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            <Avatar style={{ backgroundColor: '#52c41a', marginRight: '8px' }}>站</Avatar>
                            <Text style={{ color: 'white', fontSize: '16px' }}>站点管理员 - 专业功能</Text>
                        </div>
                    </Col>
                </Row>

                <Button
                    type="primary"
                    size="large"
                    icon={<PlayCircleOutlined />}
                    onClick={() => setShowLoginModal(true)}
                    style={{
                        height: '64px',
                        fontSize: '20px',
                        padding: '0 48px',
                        background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
                        border: 'none',
                        borderRadius: '32px',
                        boxShadow: '0 8px 24px rgba(238, 90, 36, 0.4)'
                    }}
                >
                    开始免费试用
                </Button>
            </div>

            {/* 试用登录模态框 */}
            <Modal
                open={showLoginModal}
                onCancel={() => setShowLoginModal(false)}
                footer={null}
                width={1200}
                centered
                style={{ top: 20 }}
                styles={{
                    body: { padding: 0 },
                    mask: { backdropFilter: 'blur(10px)' }
                }}
            >
                <LoginPage />
            </Modal>
        </div>
    );
};

export default LandingPage; 